import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNotificationBar } from 'context/notificationContext'
import { FormProvider, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { useSearchCapsules, useBulkCreateSearchCapsule, useBulkUpdateSearchCapsule, useBulkDeleteSearchCapsule, useToggleCapsuleStatus } from 'hooks/search-capsules'
import { useShowsSearch } from 'hooks/shows-master'
import { useCollectionSearch } from 'hooks/collection'
import { useCategorySearch } from 'hooks/category-manage'
import InputField from 'components/styledFormElements/InputField'
import { SearchMultiSelectDropdown, CapsuleItemList } from 'components/searchCapsuleForm'
import { XMarkIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { ConfirmModal } from 'components/confirmationPopup'
import { CustomTable } from 'components/customTable'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { LoaderMain } from 'components/loader'
import { RenderStatus, RenderDefaultActions } from '../../common'
import { SmoothTransition } from 'components/pageTransition'
import { parseSearchParams, getUpdatedSearchParams } from 'services/util'
import { GroupedSearchCapsule, ItemType, BulkCreatePayload } from 'interfaces/search-capsules'

interface CapsuleFormData {
  phrase: string
  selectedShows: any[]
  selectedCollections: any[]
  selectedCategories: any[]
}

const Capsules = () => {
  const notification = useNotificationBar()

  // Filter Params from URL
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams: any = parseSearchParams(searchParams, 'filter')

  // Fetch data with filter params
  const { data: capsulesData, isLoading, refetch } = useSearchCapsules(filterParams, {
    keepPreviousData: true,
  })

  const bulkCreateCapsule = useBulkCreateSearchCapsule()
  const bulkUpdateCapsule = useBulkUpdateSearchCapsule()
  const bulkDeleteCapsule = useBulkDeleteSearchCapsule()
  const toggleStatus = useToggleCapsuleStatus()

  const methods = useForm<CapsuleFormData>({
    defaultValues: {
      phrase: '',
      selectedShows: [],
      selectedCollections: [],
      selectedCategories: []
    }
  })

  const { register, handleSubmit, reset, formState: { errors } } = methods
  const filterFormHook = useForm<any>({
    defaultValues: filterParams,
  })

  const rowSelectionState = useState<RowSelectionState>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingCapsule, setEditingCapsule] = useState<GroupedSearchCapsule | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [phraseToDelete, setPhraseToDelete] = useState<string | null>(null)

  const handleAddNew = () => {
    setEditingCapsule(null)
    reset({
      phrase: '',
      selectedShows: [],
      selectedCollections: [],
      selectedCategories: []
    })
    setShowModal(true)
  }

  const handleEdit = useCallback((capsuleData: GroupedSearchCapsule) => {
    setEditingCapsule(capsuleData)
    
    const shows = capsuleData.items.filter(item => item.item_type === ItemType.SHOW).map(item => ({
      id: item.item_id!,
      name: item.name || `Show ${item.item_id?.substring(0, 8)}`,
      thumbnail: item.thumbnail,
      type: 'show'
    }))
    const collections = capsuleData.items.filter(item => item.item_type === ItemType.COLLECTION).map(item => ({
      id: item.item_id!,
      name: item.name || `Collection ${item.item_id?.substring(0, 8)}`,
      thumbnail: item.thumbnail,
      type: 'collection'
    }))
    const categories = capsuleData.items.filter(item => item.item_type === ItemType.CATEGORY).map(item => ({
      id: item.item_id!,
      name: item.name || `Category ${item.item_id?.substring(0, 8)}`,
      thumbnail: item.thumbnail,
      type: 'category'
    }))
    
    reset({
      phrase: capsuleData.phrase,
      selectedShows: shows,
      selectedCollections: collections,
      selectedCategories: categories
    })
    setShowModal(true)
  }, [reset])

  const handleDeleteConfirm = async () => {
    if (!phraseToDelete) return

    try {
      await bulkDeleteCapsule.mutateAsync({
        phrase: phraseToDelete
      })
      notification.push({
        title: 'Success',
        type: 'success',
        message: 'Capsule deleted successfully'
      })
      refetch()
    } catch (error: any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message || 'Failed to delete'
      })
    } finally {
      setDeleteConfirmOpen(false)
      setPhraseToDelete(null)
    }
  }

  const onSubmit = async (data: CapsuleFormData) => {
    setIsSubmitting(true)
    try {
      const items = [
        ...data.selectedShows.map(item => ({ item_id: item.id, item_type: ItemType.SHOW })),
        ...data.selectedCollections.map(item => ({ item_id: item.id, item_type: ItemType.COLLECTION })),
        ...data.selectedCategories.map(item => ({ item_id: item.id, item_type: ItemType.CATEGORY }))
      ]

      if (editingCapsule) {
        // Use bulk update endpoint for editing
        const updatePayload = {
          oldPhrase: editingCapsule.phrase,
          newPhrase: data.phrase,
          items: items,
          status: 'ACTIVE'
        }
        await bulkUpdateCapsule.mutateAsync({ data: updatePayload })
      } else {
        // Use bulk create endpoint for new capsules
        const payload: BulkCreatePayload = {
          phrase: data.phrase,
          items: items,
          status: 'ACTIVE'
        }
        await bulkCreateCapsule.mutateAsync({ data: payload })
      }
      
      notification.push({
        title: 'Success',
        type: 'success',
        message: `Capsule ${editingCapsule ? 'updated' : 'created'} successfully`
      })

      refetch()
      setShowModal(false)
      setEditingCapsule(null)
      reset()
    } catch (error: any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message || `Failed to ${editingCapsule ? 'update' : 'create'} capsule`
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleStatus = useCallback(async (capsuleData: GroupedSearchCapsule) => {
    try {
      const currentStatus = capsuleData.status
      const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'

      for (const item of capsuleData.items) {
        await toggleStatus.mutateAsync({
          capsule_id: item.id,
          status: newStatus
        })
      }
      
      notification.push({
        title: 'Success',
        type: 'success',
        message: `Capsule ${currentStatus === 'ACTIVE' ? 'deactivated' : 'activated'} successfully`
      })
      refetch()
    } catch (error: any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message || 'Failed to update capsule status'
      })
    }
  }, [toggleStatus, notification, refetch])

  const handleDeleteClick = useCallback((capsuleData: GroupedSearchCapsule) => {
    setPhraseToDelete(capsuleData.phrase)
    setDeleteConfirmOpen(true)
  }, [])


  const onAction = useCallback((actionType: string, rowData: any) => {
    if (actionType === 'edit') {
      handleEdit(rowData)
    }
    if (actionType === 'delete') {
      handleDeleteClick(rowData)
    }
  }, [handleEdit, handleDeleteClick])


  const columns = useMemo<ColumnDef<GroupedSearchCapsule>[]>(
    () => [
      {
        id: 'phrase',
        header: 'PHRASE',
        cell: ({ row }) => (
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {row.original.phrase}
          </span>
        ),
      },
      {
        id: 'itemsCount',
        header: 'ITEMS COUNT',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900 dark:text-gray-100">
            {row.original.items.length} item{row.original.items.length !== 1 ? 's' : ''}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'STATUS',
        cell: ({ row }) => {
          const normalizedRow = {
            ...row,
            original: {
              ...row.original,
              status: row.original.status === 'ACTIVE' ? 1 : 0
            }
          }
          return RenderStatus(normalizedRow, 'status', () => handleToggleStatus(row.original))
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'CREATED AT',
        cell: ({ row }) => (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'ACTIONS',
        cell: ({ row }) => RenderDefaultActions(row, ['edit', 'delete'], onAction),
      },
    ],
    [handleToggleStatus, onAction]
  )

  // Sync form changes with URL params
  useEffect(() => {
    const subscriber = filterFormHook.watch((values, { name }) => {
      if (name) {
        let updated = getUpdatedSearchParams(filterParams, values, name)
        if (Object.keys(updated)?.length) {
          let sp = encodeURIComponent(JSON.stringify(updated))
          setSearchParams({ filter: sp })
        } else {
          setSearchParams({})
        }
      }
    })
    return () => subscriber.unsubscribe()
  }, [filterFormHook, filterParams, setSearchParams])

  // Filter options for status
  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { display: 'All', value: '' },
        { display: 'Active', value: 'ACTIVE' },
        { display: 'Inactive', value: 'INACTIVE' },
      ],
    },
  ]

  const responseData = useMemo(() => {
    if (!capsulesData?.data) {
      return {
        results: [],
        pages: 1,
        total: 0,
        pagination: { pageSize: 10, pageIndex: 0 }
      }
    }

    const data = capsulesData.data
    return {
      ...data,
      results: Array.isArray(data.results) ? data.results : [],
    }
  }, [capsulesData])

  const tableData = useMemo(() => {
    const flatData: any[] = []
    responseData.results.forEach((capsule: GroupedSearchCapsule) => {
      flatData.push(capsule)
    })
    return flatData
  }, [responseData.results])

  if (isLoading) {
    return <LoaderMain />
  }

  return (
    <SmoothTransition>
      <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg shadow">
        <CustomTable
          columns={columns}
          data={tableData}
          pages={responseData.pages}
          totalRecords={responseData.total}
          rowSelectionState={rowSelectionState}
          isLoading={isLoading}
          formHook={filterFormHook}
          title="Search Capsules"
          onAddAction={handleAddNew}
          addButtonText="Add New Capsule"
          onRefresh={refetch}
          searchVisible={true}
          filterOptions={filterOptions}
        />

        {/* Add Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {editingCapsule ? 'Edit Capsule' : 'Add New Capsule'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false)
                    setEditingCapsule(null)
                    reset()
                  }}
                  className="float-right bg-blue-100 dark:bg-slate-700 dark:text-white text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 font-medium rounded-full text-sm p-1 text-center inline-flex items-center "
                >
                  <XMarkIcon className='float-right h-4 w-4' />
                </button>
              </div>

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                  <div>
                    <InputField
                      label="Capsule Phrase"
                      name="phrase"
                      register={register('phrase', {
                        required: 'Phrase is required',
                        minLength: {
                          value: 1,
                          message: 'Phrase cannot be empty'
                        }
                      })}
                      error={errors.phrase?.message}
                      placeholder="Enter search phrase..."
                      required
                    />
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <SearchMultiSelectDropdown
                        useSearchHook={useCategorySearch}
                        placeholder="Search categories..."
                        label="Categories"
                        itemType="category"
                        fieldName="selectedCategories"
                      />
                      <SearchMultiSelectDropdown
                        useSearchHook={useCollectionSearch}
                        placeholder="Search collections..."
                        label="Collections"
                        itemType="collection"
                        fieldName="selectedCollections"
                      />
                      <SearchMultiSelectDropdown
                        useSearchHook={useShowsSearch}
                        placeholder="Search shows..."
                        label="Shows"
                        itemType="show"
                        fieldName="selectedShows"
                      />
                    </div>

                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">
                        📦 Selected Items
                      </h4>
                      <CapsuleItemList />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false)
                        setEditingCapsule(null)
                        reset()
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : editingCapsule ? 'Update' : 'Save'}
                    </button>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          open={deleteConfirmOpen}
          title="Delete Capsule"
          message="Are you sure you want to delete this capsule? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onCancel={() => {
            setDeleteConfirmOpen(false)
            setPhraseToDelete(null)
          }}
          onConfirm={handleDeleteConfirm}
        />
      </div>
    </SmoothTransition>
  )
}

export default Capsules
